import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "../ui/sonner";
import { LogOut, RefreshCw, Mail, Building2, MessageSquareText, CalendarDays, Inbox, Loader2, ShieldX } from "lucide-react";
import { Logo } from "./Logo";
import { WhatsAppIcon } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const waLink = (phone) => `https://wa.me/55${String(phone).replace(/\D/g, "")}`;

const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleButton = ({ testId = "admin-google-login-btn" }) => (
  <button
    data-testid={testId}
    onClick={loginWithGoogle}
    className="glow-blue inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-slate-800 transition-all duration-300 hover:scale-[1.02] hover:bg-slate-100"
  >
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09Z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
    </svg>
    Entrar com Google
  </button>
);

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = (location.hash.split("session_id=")[1] || "").split("&")[0];
    const exchange = async () => {
      try {
        const { data } = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        navigate("/admin", { state: { user: data }, replace: true });
      } catch (e) {
        if (e.response?.status === 403) {
          setError("Esta conta Google não tem acesso ao painel. Entre com a conta autorizada da Sapientia.");
        } else {
          setError("Não foi possível concluir o login. Tente novamente.");
        }
      }
    };
    exchange();
  }, [location, navigate]);

  return (
    <div data-testid="auth-callback" className="flex min-h-screen items-center justify-center bg-tria-ink px-5 font-body">
      <div className="glass-card flex w-full max-w-sm flex-col items-center gap-4 rounded-3xl p-8 text-center">
        <Logo compact />
        {error ? (
          <>
            <ShieldX className="h-8 w-8 text-rose-400" />
            <p data-testid="auth-callback-error" className="text-[13.5px] leading-relaxed text-rose-300">{error}</p>
            <a href="/admin" data-testid="auth-callback-retry" className="text-[13px] font-semibold text-tria-sky underline decoration-tria-sky/40 underline-offset-4 hover:text-white">
              Voltar para o login
            </a>
          </>
        ) : (
          <>
            <Loader2 className="h-7 w-7 animate-spin text-tria-sky" />
            <p className="text-[13.5px] text-tria-mist">Confirmando sua conta Google…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default function AdminLeads() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? "authenticated" : "checking");
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    if (authState !== "checking") return;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        setAuthState("authenticated");
      } catch {
        setAuthState("anonymous");
      }
    };
    check();
  }, [authState]);

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthState("anonymous");
        setUser(null);
        setLeads(null);
      } else {
        toast.error("Não foi possível carregar os leads.");
      }
    }
  }, []);

  useEffect(() => {
    if (authState === "authenticated") loadLeads();
  }, [authState, loadLeads]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* sessão já inválida */ }
    setAuthState("anonymous");
    setUser(null);
    setLeads(null);
  };

  if (authState === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tria-ink">
        <Loader2 className="h-7 w-7 animate-spin text-tria-sky" />
      </div>
    );
  }

  if (authState === "anonymous") {
    return (
      <div data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center bg-tria-ink px-5 font-body">
        <div className="pointer-events-none absolute left-1/2 top-[-200px] h-[420px] w-[640px] -translate-x-1/2 rounded-full bg-tria-vivid/15 blur-[130px]" />
        <Toaster theme="dark" position="top-center" richColors />
        <div className="glass-card relative w-full max-w-sm rounded-3xl p-8">
          <div className="mb-7 flex flex-col items-center gap-4 text-center">
            <Logo compact />
            <div>
              <h1 className="font-display text-xl font-semibold text-white">Painel de Leads</h1>
              <p className="mt-1 text-[12.5px] text-tria-subtle">Acesso exclusivo da equipe Sapientia</p>
            </div>
          </div>
          <GoogleButton />
          <p className="mt-4 text-center text-[11.5px] leading-relaxed text-tria-subtle">
            Somente a conta Google autorizada consegue entrar neste painel.
          </p>
          <a href="/" data-testid="admin-back-link" className="mt-5 block text-center text-[12px] font-medium text-tria-subtle transition-colors hover:text-tria-sky">
            ← Voltar para o site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="relative min-h-screen bg-tria-ink font-body text-white">
      <div className="pointer-events-none absolute left-1/2 top-[-220px] h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-tria-vivid/12 blur-[140px]" />
      <Toaster theme="dark" position="top-center" richColors />
      <header className="sticky top-0 z-10 border-b border-white/[0.07] bg-tria-navy/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between px-5 sm:px-8">
          <Logo compact />
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden items-center gap-2.5 sm:flex" data-testid="admin-user-badge">
                {user.picture ? (
                  <img src={user.picture} alt={`Foto de ${user.name}`} className="h-8 w-8 rounded-full border border-white/15" referrerPolicy="no-referrer" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-tria-vivid/25 text-[12px] font-bold text-tria-sky">
                    {(user.name || user.email || "A").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="max-w-[140px] truncate text-[12.5px] font-medium text-tria-mist">{user.name || user.email}</span>
              </div>
            )}
            <button
              data-testid="admin-refresh-btn"
              onClick={loadLeads}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[13px] font-semibold text-white transition-all hover:border-tria-sky/50 hover:bg-white/5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Atualizar
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[13px] font-semibold text-tria-mist transition-all hover:border-rose-400/40 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-tria-sky">Pedidos de demonstração</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-white">Leads da landing page</h1>
          </div>
          <div data-testid="leads-count" className="glass-card rounded-2xl px-5 py-3.5 text-center">
            <p className="font-mono text-2xl font-bold text-white">{leads ? leads.length : "—"}</p>
            <p className="text-[10.5px] font-medium text-tria-subtle">interessados</p>
          </div>
        </div>

        {leads === null ? (
          <p className="py-20 text-center text-sm text-tria-subtle">Carregando…</p>
        ) : leads.length === 0 ? (
          <div data-testid="leads-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-white/[0.07] bg-white/[0.02] py-20 text-center">
            <Inbox className="h-8 w-8 text-tria-subtle" />
            <p className="text-sm text-tria-mist">Nenhum lead por aqui ainda. Os pedidos de demonstração aparecerão nesta lista.</p>
          </div>
        ) : (
          <div data-testid="leads-list" className="grid gap-4 md:grid-cols-2">
            {leads.map((lead, i) => (
              <div key={lead.id || i} data-testid={`lead-card-${i + 1}`} className="group rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6 transition-all duration-300 hover:border-tria-sky/30">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-[16px] font-semibold text-white">{lead.nome}</h3>
                    {lead.tipo_negocio && (
                      <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-tria-sky/25 bg-tria-sky/[0.07] px-2.5 py-0.5 text-[10.5px] font-semibold text-tria-sky">
                        <Building2 className="h-3 w-3" />
                        {lead.tipo_negocio}
                      </span>
                    )}
                    {lead.origem === "simulacao" && (
                      <span data-testid="lead-badge-simulacao" className="ml-1.5 mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-amber-400/25 bg-amber-400/[0.07] px-2.5 py-0.5 text-[10.5px] font-semibold text-amber-300">
                        Simulação do método
                      </span>
                    )}
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1.5 font-mono text-[10px] text-tria-subtle">
                    <CalendarDays className="h-3 w-3" />
                    {fmtDate(lead.created_at)}
                  </span>
                </div>
                <div className="mt-4 space-y-2 text-[13px]">
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2.5 text-tria-mist transition-colors hover:text-white">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-tria-subtle" />
                    {lead.email}
                  </a>
                  <a href={waLink(lead.whatsapp)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-tria-mist transition-colors hover:text-emerald-400">
                    <WhatsAppIcon className="h-3.5 w-3.5 shrink-0 text-tria-subtle" />
                    {lead.whatsapp}
                  </a>
                </div>
                {lead.mensagem && (
                  <p className="mt-4 flex items-start gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-[12.5px] leading-relaxed text-tria-mist">
                    <MessageSquareText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tria-subtle" />
                    {lead.mensagem}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
